import { jsx, Fragment } from 'react/jsx-runtime';
import isFunction from 'lodash/isFunction.js';
import React, { useCallback, useEffect, useRef, useState, useLayoutEffect, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty.js';
import union from 'lodash/union.js';
import without from 'lodash/without.js';
import debounce from 'lodash/debounce.js';
import throttle from 'lodash/throttle.js';
import isObject from 'lodash/isObject.js';
import * as Router from 'next/dist/client/router.js';
import { AnimatePresence } from 'framer-motion';

const Fac = ({ render, props }) => /* @__PURE__ */ jsx(Fragment, { children: isFac(render) ? render(props) : render });
const isFac = isFunction;
Fac.isFac = isFac;

const FontDocument = ({ fonts }) => /* @__PURE__ */ jsx(Fragment, { children: fonts.map((font) => {
  return /* @__PURE__ */ jsx("link", { href: font.src, rel: "stylesheet", type: "text/css" }, font.src);
}) });

const useOnClickOutsideEffect = (ref, onClickOutside, condition) => {
  const closeOnClickOutside = useCallback(
    (e) => {
      if (!ref.current || !e.target || condition !== true)
        return;
      if (!ref.current?.contains(e.target)) {
        onClickOutside(e);
      }
    },
    [onClickOutside, condition, ref?.current]
  );
  useEffect(() => {
    if (condition !== true)
      return;
    window.addEventListener("click", closeOnClickOutside);
    return () => window.removeEventListener("click", closeOnClickOutside);
  }, [condition, closeOnClickOutside]);
};
const useOnClickOutside = (onClickOutside, condition) => {
  const ref = useRef(null);
  useOnClickOutsideEffect(ref, onClickOutside, condition);
  return ref;
};

const useDelayedState = (defaultValue) => {
  const timeoutInRef = useRef();
  const [state, setState] = useState(defaultValue);
  const setDelayedState = useCallback((state2, timeout) => {
    if (timeout) {
      timeoutInRef.current = window.setTimeout(() => setState(state2), timeout);
      return;
    }
    setState(state2);
  }, []);
  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutInRef.current);
    };
  }, []);
  return [state, setDelayedState];
};

const useDisclosure = ({ initialOpen, onOpen, onClose } = {}) => {
  const [isOpen, setOpen] = useState(initialOpen || false);
  const handleOpen = useCallback(() => {
    onOpen?.();
    setOpen(true);
  }, [onOpen]);
  const handleClose = useCallback(() => {
    onClose?.();
    setOpen(false);
  }, [onClose]);
  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
      return;
    }
    handleOpen();
  }, [isOpen, handleClose, handleOpen]);
  return { isOpen, handleOpen, handleClose, handleToggle };
};
const useListDisclosure = ({ initialOpen, multiple, onOpen, onClose } = {
  multiple: false
}) => {
  const [activeIds, setActiveIds] = useState(initialOpen);
  const isOpen = (id) => activeIds?.includes(id) || false;
  const handleOpen = (id) => () => {
    if (multiple && !isEmpty(activeIds)) {
      setActiveIds(union(activeIds, [id]));
      return;
    }
    onOpen?.();
    setActiveIds([id]);
  };
  const handleClose = (id) => () => {
    if (multiple && !isEmpty(activeIds)) {
      setActiveIds(without(activeIds, id));
      return;
    }
    onClose?.();
    setActiveIds([]);
  };
  const handleToggle = (id) => () => {
    if (activeIds?.includes(id)) {
      handleClose(id)();
      return;
    }
    handleOpen(id)();
  };
  const handleReset = () => setActiveIds([]);
  return { isOpen, handleOpen, handleClose, handleToggle, handleReset, activeIds, setActiveIds };
};

const useKeyPressEffect = (targetKey, callback, config) => {
  const handleCallback = (e) => {
    if (targetKey === "debug") {
      console.log("Key:", e.key, e);
    }
    if (e.key === targetKey) {
      callback?.(e);
    }
  };
  useEventListener("keydown", handleCallback, config);
};
const useEventListener = (type, callback, config = {}) => {
  const handleCallback = (e) => callback?.(e);
  const listener = config?.debounce ? debounce(handleCallback, config.debounce) : config?.throttle ? throttle(handleCallback, config.throttle) : handleCallback;
  useEffect(() => {
    window.addEventListener(type, listener);
    if (!callback) {
      window.removeEventListener(type, listener);
    }
    return () => {
      window.removeEventListener(type, listener);
    };
  }, config?.deps || []);
};

const useInterval = (callback, timeout) => {
  const interval = useRef();
  useEffect(() => {
    interval.current = window.setInterval(callback, timeout);
    return () => window.clearInterval(interval.current);
  }, [callback, timeout]);
};

const useIsMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const useMemoKey = (item) => {
  return useMemo(() => {
    if (!isObject(item)) {
      return item;
    }
    return JSON.stringify(item);
  }, [item]);
};

const usePreserveString = (text, { delay = 1e3 } = {}) => {
  const [preserveText, setPreserveText] = useState(text);
  useEffect(() => {
    if (text) {
      if (preserveText === text)
        return;
      setPreserveText(text);
    }
    const timeout = window.setTimeout(() => setPreserveText(void 0), delay);
    return () => window.clearTimeout(timeout);
  }, [text, delay, preserveText]);
  return preserveText;
};

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const isServer = typeof window === "undefined";
const isDevelopment = process.env["NODE_ENV"] === "development";
const isProduction = process.env["NODE_ENV"] === "production";

const prune = (obj) => {
  if (!obj)
    return;
  const newObj = { ...obj };
  const _keys = Object.keys(obj);
  _keys.forEach((k) => {
    if (newObj[k] !== void 0 && newObj[k] !== null) {
      return;
    }
    delete newObj[k];
  });
  return newObj;
};
const useQueryState = (initialState, config) => {
  const { query } = Router.useRouter() || {};
  const setState = useSetQueryState(initialState, config);
  const typedQuery = query;
  return [typedQuery, setState];
};
const useSetQueryState = (initialState, config = {}) => {
  const { query, push, replace, pathname } = Router.useRouter() || {};
  const typedQuery = query;
  const setState = (newQuery) => {
    if (isServer)
      return;
    const newQueryState = isFunction(newQuery) ? newQuery(typedQuery) : newQuery;
    const queries = prune({
      ...query,
      ...newQueryState
    });
    if (config.type === "push" || !config.type) {
      push({
        pathname,
        query: queries
      });
    }
    if (config.type === "replace") {
      replace({
        pathname,
        query: queries
      });
    }
  };
  useEffect(() => {
    if (isServer)
      return;
    if (initialState) {
      replace({
        pathname,
        query: prune({ ...initialState, ...typedQuery })
      });
    }
  }, []);
  return setState;
};
const useQueryValue = () => {
  const { query } = Router.useRouter() || {};
  const typedQuery = query;
  return typedQuery;
};

const RecordRender = ({
  render,
  renderKey,
  animatePresence = true,
  fallback,
  onFallback
}) => {
  const component = render?.[renderKey];
  useEffect(() => {
    if (!component) {
      onFallback?.();
    }
  }, [component, onFallback, renderKey]);
  if (!component && !fallback) {
    return null;
  }
  if (!animatePresence) {
    return /* @__PURE__ */ jsx(Fragment, { children: component });
  }
  return /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", children: React.cloneElement(component || fallback, {
    key: renderKey
  }) });
};

export { Fac, FontDocument, RecordRender, isDevelopment, isFac, isProduction, isServer, useDelayedState, useDisclosure, useEventListener, useInterval, useIsMounted, useIsomorphicLayoutEffect, useKeyPressEffect, useListDisclosure, useMemoKey, useOnClickOutside, useOnClickOutsideEffect, usePreserveString, usePrevious, useQueryState, useQueryValue, useSetQueryState };
